/**
 * `board_relation` (Connect boards) — `BoardRelationValue`
 * (`specs/M2-tier2-column-types.md` §5.17, `[09§A.7 board_relation]`, `[cvs§2 board_relation]`).
 *
 * **The value monday returns is always null.** Verbatim: *"`text` and `value` are always null —
 * read `linked_item_ids`"*, and all 75 sampled cells confirm it, including the 13 that *are*
 * linked. The links live only in the typed fragment (`display_value`, `linked_item_ids`), which
 * is why the importer's relation pass rebuilds them from there (`stages/relations.ts`).
 *
 * **What we store** is monday's own populated write/read shape,
 * `{"linkedPulseIds":[{"linkedPulseId":N}],"changed_at":…}` `[cvs§2 board_relation]` — the
 * importer already writes exactly this, so the two halves agree. `/v2` still serves
 * `value: null` / `text: null` (`api: { value: 'null', text: 'null' }`), so a conformance
 * replay sees what monday sends.
 *
 * The **write** payload is a different spelling again: `{"connect_boards": {"item_ids": […]}}`
 * `[09§A.7]`. Both are accepted here.
 *
 * Settings: `{"boardIds":[…], "allowMultipleItems": true, "allowCreateReflectionColumn": true}`
 * — the third is *"whether a two-way (mirror) column was created on the connected board"*, the
 * `Turn on two-way connection` button in the captured **Connected board column settings**
 * dialog (`discovery/ui-reference/editors/board_relation/dialog-settings.png`).
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql, isBlankOnly, sqlFragment } from '../sql.js';
import { textShadow } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const boardRelationValueSchema = z.looseObject({
    linkedPulseIds: z.array(z.looseObject({ linkedPulseId: z.number().int() })),
    changed_at: z.string().nullable().optional(),
});
import { boardRelationFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const boardRelationSettingsSchema = z.looseObject({
    boardIds: z.array(z.union([z.number(), z.string()])).optional(),
    allowMultipleItems: z.boolean().optional(),
    /** A two-way (mirror) column exists on the connected board `[09§A.7 board_relation]`. */
    allowCreateReflectionColumn: z.boolean().optional(),
    hide_footer: z.boolean().optional(),
});
/** The linked item ids, in order, as strings — the one thing every caller wants. */
export function linkedItemIds(value) {
    return (value?.linkedPulseIds ?? []).map((entry) => String(entry.linkedPulseId));
}
export function connectedBoardIds(settings) {
    return (settings.boardIds ?? []).map(Number).filter((id) => Number.isFinite(id));
}
function idsFrom(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (Array.isArray(raw))
        return raw.map(Number).filter((id) => Number.isInteger(id));
    if (typeof raw !== 'object')
        throw new ColumnValueError('board_relation', 'expected an object');
    const record = raw;
    if (Array.isArray(record.linkedPulseIds)) {
        return record.linkedPulseIds
            .map((entry) => entry !== null && typeof entry === 'object'
            ? Number(entry.linkedPulseId)
            : Number(entry))
            .filter((id) => Number.isInteger(id));
    }
    // The write spelling, and the read fragment's.
    const list = record.item_ids ?? record.itemIds ?? record.linked_item_ids;
    if (Array.isArray(list))
        return list.map(Number).filter((id) => Number.isInteger(id));
    return null;
}
function normalize(raw, keepVolatile) {
    const ids = idsFrom(raw);
    if (ids === null || ids.length === 0)
        return null;
    const unique = [];
    for (const id of ids)
        if (!unique.includes(id))
            unique.push(id);
    const value = { linkedPulseIds: unique.map((id) => ({ linkedPulseId: id })) };
    if (keepVolatile && raw !== null && typeof raw === 'object') {
        const source = raw;
        if ('changed_at' in source)
            value.changed_at = source.changed_at;
    }
    return value;
}
/** `display_value`: the linked items' names, joined `", "` (`"Sarah Jenkins, Emily Blunt"`). */
export function relationDisplayValue(value, ctx) {
    if (value === null)
        return '';
    return linkedItemIds(value)
        .map((id) => ctx?.resolveItemName?.(id))
        .filter((name) => name !== undefined && name !== '')
        .join(', ');
}
export const boardRelationColumn = defineColumnType({
    type: 'board_relation',
    title: 'Connect boards',
    description: 'Links items on another board.',
    apiFragmentName: 'BoardRelationValue',
    legacyTypeName: 'board-relation',
    /** monday's own id for the first one is `connect_boards`, not `board_relation` `[09§A.7 rule 2]`. */
    idPrefix: 'connect_boards',
    valueSchema: boardRelationValueSchema,
    settingsSchema: boardRelationSettingsSchema,
    defaultSettings: { allowMultipleItems: true },
    settingsStrSchema: boardRelationSettingsSchema,
    writable: true,
    /** *"The `change_simple_column_value` mutation is not supported"* `[09§A.7]`. */
    supportsSimpleValue: false,
    clearForms: ['null'],
    filterConditions: boardRelationFilterConditions,
    filterOperators: operatorsOf(boardRelationFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    /** monday returns `text: null` (not `""`) for this type, empty or not `[cvs§2]`. */
    emptyText: null,
    parseValue(raw) {
        return normalize(raw, false);
    },
    /**
     * monday's `text` is **null on every row**, so ours is too. The names live in
     * `display_value`; a caller that wants them asks `relationDisplayValue()`.
     */
    toText() {
        return null;
    },
    toSortKeys(value, _settings, ctx) {
        // The shadow still carries the names, so sorting and searching a relation column work.
        return { text: textShadow(relationDisplayValue(value, ctx)), number: null, date: null };
    },
    isEqual(a, b) {
        const left = linkedItemIds(a ?? null);
        const right = linkedItemIds(b ?? null);
        return left.length === right.length && left.every((id, index) => id === right[index]);
    },
    toActivityValue(value) {
        return value === null ? null : { linkedPulseIds: linkedItemIds(value).map(Number) };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { linkedPulseIds: value.linkedPulseIds.map((entry) => ({ ...entry })) };
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return boardRelationSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    /**
     * **An empty cell still answers every non-nullable field of its fragment.** monday returns
     * the zero values, not an absent key: recording `0261` answers an unset `dependency` and an
     * unset `board_relation` with `{display_value: "", linked_item_ids: [], linked_items: []}`,
     * `0260` answers an unset `tags` with `{tags: []}` and `0263` answers an unset
     * `time_tracking` with `{running: false, started_at: null, duration: 0, history: []}`.
     * Returning `{}` leaves the non-nullable fields `undefined`, and **one** empty cell then
     * nulls the whole `items` response (T-5s, M9 §12.2a F-1).
     *
     * The three fragment fields that are **rows** rather than projections —
     * `linked_items`, `subitems` and `history` — are resolved by `/v2` itself
     * (`apps/api/src/graphql/v2/column-value-fields.ts`): the registry has no database.
     */
    toApiFragment(value, _settings, ctx) {
        return {
            display_value: relationDisplayValue(value, ctx),
            linked_item_ids: linkedItemIds(value),
        };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /** Facet by linked **item id**, like `dropdown` facets by option id. */
    bucketsOf(value, _settings, ctx) {
        return linkedItemIds(value).map((id) => ({ key: id, label: ctx?.nameOf?.(id) ?? `#${id}` }));
    },
    /** `any_of` compares linked item ids inside the stored array (GIN containment). */
    filterSql(ctx) {
        const cell = ctx.refs?.cell ?? 'cv';
        switch (ctx.operator) {
            case 'is_empty':
                return sqlFragment(`(${cell}.item_id IS NULL OR ${cell}.value IS NULL)`);
            case 'is_not_empty':
                return sqlFragment(`${cell}.value IS NOT NULL`);
            case 'any_of':
            case 'not_any_of': {
                if (isBlankOnly(ctx.compareValues)) {
                    return ctx.operator === 'any_of'
                        ? sqlFragment(`(${cell}.item_id IS NULL OR ${cell}.value IS NULL)`)
                        : sqlFragment(`${cell}.value IS NOT NULL`);
                }
                const ids = ctx.compareValues.map(Number).filter((id) => Number.isInteger(id));
                if (ids.length === 0)
                    return null;
                const test = ids.map(() => `${cell}.value -> 'linkedPulseIds' @> ?::jsonb`).join(' OR ');
                const params = ids.map((id) => JSON.stringify([{ linkedPulseId: id }]));
                return ctx.operator === 'any_of'
                    ? sqlFragment(`(${test})`, params)
                    : sqlFragment(`(${cell}.item_id IS NULL OR NOT (${test}))`, params);
            }
            default:
                // The three text rows compare the linked items' names — the `text_value` shadow.
                return defaultFilterSql('text', ctx);
        }
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map